import { cn } from "@/lib/utils";
import { formatLocalDate, weekOfLabel } from "../../../../shared/local-date";
import type { FormSnapshotData } from "../../../../shared/form-types";
import {
  deriveChangeLine,
  formValueLabel,
  getFormEnumFields,
  narrativeEntries,
  toneForFormValue,
  variantLabel,
  type FormTone,
} from "../../../../shared/form-registry";

/**
 * Shared, hook-free read-only rendering of a weekly form snapshot, per the comp
 * #snapshot-flyover body. Pure so it renders in both the client flyover
 * (FormSnapshotFlyover) and the server full-page view. Enum answers read as
 * plain text with only the Status pill toned (ADR-0004 — tone is severity, never
 * category, and always paired with its label); a quiet change line notes
 * week-over-week movement; narrative fields are labelled note blocks.
 */

const TONE_PILL: Record<FormTone, string> = {
  quiet: "bg-sand-100 text-sand-600",
  warn: "bg-sunlight-100 text-sunlight-500",
  risk: "bg-orange-100 text-orange-500",
};

/** The single toned pill in the read-only view — Status only. */
export function FormStatusPill({ value }: { value: string | null }) {
  if (!value) return <span className="font-normal text-sand-400">—</span>;
  const tone = toneForFormValue(value);
  return (
    <span
      className={cn(
        "inline-flex min-h-[22px] w-fit items-center justify-center whitespace-nowrap rounded-full px-[9px] text-[11px] font-bold",
        TONE_PILL[tone],
      )}
    >
      {formValueLabel(value)}
    </span>
  );
}

/**
 * Read-only kv label overrides (comp #snapshot-flyover). The capture form uses
 * "Overall RD confidence"; the flyover/full-page kv row uses the shorter
 * "Overall confidence" to fit the 148px key column, per the binding comp. The
 * registry label (capture form) is unchanged.
 */
const KV_LABEL_OVERRIDES: Record<string, string> = {
  overallConfidence: "Overall confidence",
};

/** Read the queryable enum answer for a registry field key off the snapshot. */
function enumValue(snapshot: FormSnapshotData, key: string): string | null {
  switch (key) {
    case "status":
      return snapshot.status;
    case "trajectory":
      return snapshot.trajectory;
    case "overallConfidence":
      return snapshot.overallConfidence;
    case "counselingDevelopment":
      return snapshot.counselingDevelopment;
    case "clinicalDirectorAttention":
      return snapshot.clinicalDirectorAttention;
    default:
      return null;
  }
}

/** "Week of {short date}" — the snapshot's period label. */
export function snapshotWeekLabel(snapshot: FormSnapshotData): string {
  return weekOfLabel(snapshot.periodStartDate);
}

/**
 * Origin line — "{Variant} · v{version} · by {supervisor}, {created date}"
 * plus an optional provenance tail ("Opened from Functional Forms" in the
 * flyover; omitted on the full page where the breadcrumb already says so).
 */
export function formSnapshotOriginLine(
  snapshot: FormSnapshotData,
  tail?: string,
): string {
  const parts = [
    variantLabel(snapshot.formVariant),
    `v${snapshot.formVersion}`,
    `by ${snapshot.supervisorName || "supervisor"}, ${formatLocalDate(
      snapshot.createdAt.slice(0, 10),
      "short",
    )}`,
  ];
  if (tail) parts.push(tail);
  return parts.join(" · ");
}

const BLOCK =
  "rounded-xl border border-sand-200 bg-card px-[14px] py-[13px] shadow-[0_1px_2px_rgba(40,3,15,0.05)]";
const BLOCK_LABEL =
  "mb-2 font-sans text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-sand-400";
const NOTE_LABEL =
  "font-sans text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-sand-500";

/** The weekly snapshot immediately preceding `snapshot` in a newest-first list. */
export function priorWeeklySnapshot(
  snapshots: FormSnapshotData[],
  snapshot: FormSnapshotData | null,
): FormSnapshotData | null {
  if (!snapshot || snapshot.formType !== "weekly_form") return null;
  const index = snapshots.findIndex((s) => s.id === snapshot.id);
  if (index < 0) return null;
  return snapshots.slice(index + 1).find((s) => s.formType === "weekly_form") ?? null;
}

/**
 * The two read-only item-blocks: "This week" (enum answers + change line) and
 * the narrative note blocks. `prior` is the preceding weekly snapshot used to
 * derive the change line; pass null when there is none.
 */
export function FormSnapshotBody({
  snapshot,
  prior,
}: {
  snapshot: FormSnapshotData;
  prior?: FormSnapshotData | null;
}) {
  const enumFields = getFormEnumFields(
    snapshot.formType,
    snapshot.formVariant,
    snapshot.formVersion,
  );
  const changeLine = deriveChangeLine(snapshot, prior ?? null);
  const narrative = narrativeEntries(snapshot.response).filter(
    (entry) => entry.value.trim().length > 0,
  );

  return (
    <div className="flex flex-col gap-3">
      <section className={BLOCK}>
        <p className={BLOCK_LABEL}>This week</p>
        {enumFields.map((field) => {
          const value = enumValue(snapshot, field.key);
          return (
            <div
              key={field.key}
              className="grid grid-cols-[148px_minmax(0,1fr)] gap-[10px] py-[5px] text-[12.5px]"
            >
              <span className="text-sand-500">
                {KV_LABEL_OVERRIDES[field.key] ?? field.label}
              </span>
              <span className="font-semibold text-foreground">
                {field.key === "status" ? (
                  <FormStatusPill value={value} />
                ) : value ? (
                  formValueLabel(value)
                ) : (
                  <span className="font-normal text-sand-400">—</span>
                )}
              </span>
            </div>
          );
        })}
        {changeLine && (
          <p className="mt-1 text-[13px] leading-[1.55] text-sand-600">
            {changeLine}
          </p>
        )}
      </section>

      {narrative.length > 0 && (
        <section className={BLOCK}>
          {narrative.map((entry, index) => (
            <div key={entry.key}>
              <p className={cn(NOTE_LABEL, index === 0 ? "mt-0" : "mt-3", "mb-1")}>
                {entry.label}
              </p>
              <p className="whitespace-pre-wrap text-[13px] leading-[1.55] text-sand-600">
                {entry.value}
              </p>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
